import { FC } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton
} from "@mui/x-data-grid-premium";
import { ExportMenu } from "./exportMenu";

// Builds a Data Grid `toolbar` slot with a collapsible quick-filter search box,
// reusable across the counterparties, brokers and (later) products grids. The quick
// filter matches the typed text against every column's value, so a single field
// searches the whole row. Collapsed to a search icon by default; clicking the icon
// expands the field, which collapses again on blur when empty (built into
// QuickFilterControl). The trigger and the input share one grid cell so the input
// animates open over the icon.
//
// Call it once with the grid's search label to get a stable slot component, e.g.
//   const Toolbar = makeQuickFilterToolbar("Search brokers");
//   <DataGrid showToolbar slots={{ toolbar: Toolbar }} />
// The shared DataGrid wrapper wires this up for you via its `searchPlaceholder` prop.
// Pass `exportFileName` to add a CSV / Excel export menu next to the search box.
export const makeQuickFilterToolbar = (
  placeholder: string,
  exportFileName?: string
): FC => {
  const QuickFilterToolbar: FC = () => (
    <Toolbar>
      <QuickFilter
        render={(props) => (
          <Box {...props} sx={{ display: "grid", alignItems: "center" }} />
        )}
      >
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title={placeholder} enterDelay={0}>
              <ToolbarButton
                {...triggerProps}
                aria-label={placeholder}
                aria-disabled={state.expanded}
                material={{
                  sx: {
                    gridArea: "1 / 1",
                    width: "min-content",
                    height: "min-content",
                    zIndex: 1,
                    opacity: state.expanded ? 0 : 1,
                    pointerEvents: state.expanded ? "none" : "auto",
                    transition: (theme) => theme.transitions.create(["opacity"])
                  }
                }}
              >
                <SearchIcon fontSize="small" />
              </ToolbarButton>
            </Tooltip>
          )}
        />
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <TextField
              {...controlProps}
              inputRef={ref}
              size="small"
              aria-label={placeholder}
              placeholder={placeholder}
              sx={{
                gridArea: "1 / 1",
                overflowX: "clip",
                width: state.expanded ? 280 : 0,
                opacity: state.expanded ? 1 : 0,
                m: state.expanded ? 1 : 0,
                transition: (theme) =>
                  theme.transitions.create(["width", "opacity", "margin"])
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        render={
                          <IconButton
                            size="small"
                            edge="end"
                            aria-label="Clear search"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        }
                      />
                    </InputAdornment>
                  ) : null
                },
                // Preserve QuickFilterControl's own slotProps (htmlInput carries the
                // built-in blur-to-collapse handler, role, id and tabIndex).
                ...controlProps.slotProps
              }}
            />
          )}
        />
      </QuickFilter>
      {exportFileName ? <ExportMenu fileName={exportFileName} /> : null}
    </Toolbar>
  );

  QuickFilterToolbar.displayName = "QuickFilterToolbar";
  return QuickFilterToolbar;
};
